<script setup lang="ts">
  import type { StrapiRegistrationData } from '@nuxtjs/strapi'
  import { notify } from 'notiwind'
  import type { ApplyProfile, CvExtraction, SignupDocuments } from '~/types/apply'
  import { handleError } from '~/utils/error'

  // Leave room for the fixed stepper.
  definePageMeta({ noFooter: true })
  useTitle('Création de compte')

  const route = useRoute()
  const auth = useStrapiAuth()
  const { user } = useUser()
  const { extractCv } = useCvExtraction()

  // Page the visitor signed up from: the login opens there once the account is created.
  // Only same-site paths are accepted since the value comes from the URL.
  const returnPath = computed(() => {
    const value = Array.isArray(route.query.redirect) ? route.query.redirect[0] : route.query.redirect
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/inscription')) return '/'
    return value.split('#')[0]
  })

  onMounted(() => {
    if (user.value) navigateTo(returnPath.value, { replace: true })
  })

  const Steps = { DOCUMENTS: 'documents', PROFILE: 'profile' } as const
  const stepperSteps = [
    { key: Steps.DOCUMENTS, label: 'Mon compte' },
    { key: Steps.PROFILE, label: 'Mon profil' }
  ]

  const step = ref<(typeof Steps)[keyof typeof Steps]>(Steps.DOCUMENTS)
  const analysing = ref(false)
  const submitting = ref(false)
  const documents = ref<SignupDocuments | null>(null)
  const extraction = ref<CvExtraction | null>(null)

  const onDocumentsNext = async (values: SignupDocuments) => {
    documents.value = values
    step.value = Steps.PROFILE
    analysing.value = true
    extraction.value = await extractCv(values.cv as File)
    analysing.value = false
  }

  const onDocumentsManual = (values: SignupDocuments) => {
    documents.value = values
    extraction.value = null
    step.value = Steps.PROFILE
  }

  const appendDefined = (formData: FormData, key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return
    formData.append(key, String(value))
  }

  const onProfileSubmit = async (p: ApplyProfile) => {
    if (submitting.value || !documents.value) return
    submitting.value = true
    const d = documents.value
    try {
      // Account, candidate profile and files in one request, as on /depot-cv.
      const formData = new FormData()
      appendDefined(formData, 'username', p.email)
      appendDefined(formData, 'email', p.email)
      appendDefined(formData, 'password', d.password)
      appendDefined(formData, 'gender', p.gender)
      appendDefined(formData, 'firstName', p.firstName)
      appendDefined(formData, 'lastName', p.lastName)
      appendDefined(formData, 'phone', p.phone)
      formData.append('newsletter', String(!!d.newsletter))
      formData.append('cgu', 'true')
      if (p.linkedin) formData.append('networks', JSON.stringify({ linkedin: p.linkedin }))
      formData.append(
        'candidateData',
        JSON.stringify({
          gender: p.gender,
          firstName: p.firstName,
          lastName: p.lastName,
          email: p.email,
          phone: p.phone,
          linkedin: p.linkedin,
          location: p.location,
          summary: p.summary,
          official: p.official,
          // Exams only apply to declared public servants.
          exam: p.official === true ? p.exam : null,
          experience: p.experience,
          sectors: p.sectors,
          subSectors: p.subSectors,
          expertises: p.expertises,
          drivingLicenses: p.drivingLicenses,
          languages: p.languages
        })
      )
      if (d.cv) formData.append('files.cv', d.cv)
      if (p.avatar) formData.append('files.avatar', p.avatar)
      // The module type only describes JSON registration, not multipart data.
      await auth.register(formData as unknown as StrapiRegistrationData)

      await navigateTo(`${returnPath.value}#connexion`, { replace: true })
      notify(
        {
          group: 'main',
          type: 'success',
          title: 'Inscription réussie',
          text: `Pour confirmer votre inscription, cliquez sur le lien qui vous a été envoyé par mail à l’adresse <b>${p.email}</b> (pensez à vérifier aussi vos spams)`
        },
        5000
      )
    } catch (err) {
      const error = err.error || err
      handleError(error, error.message === 'Email or Username are already taken' ? 'Email déjà utilisé - connectez-vous.' : true)
    } finally {
      submitting.value = false
    }
  }
</script>

<template>
  <!-- Short screens are centered between the header and the fixed stepper; the long profile form scrolls. -->
  <div
    class="pb-24 pt-6"
    :class="{ 'flex min-h-[calc(100svh-4rem)] flex-col justify-center md:min-h-[calc(100svh-5rem)]': step === Steps.DOCUMENTS || analysing }"
  >
    <div class="px-4">
      <ApplyStepSignup v-if="step === Steps.DOCUMENTS" :login-path="returnPath" @next="onDocumentsNext" @manual="onDocumentsManual" />

      <template v-else>
        <ApplyAnalysing v-if="analysing" title="Création de compte" />
        <ApplyStepProfile
          v-else
          signup
          title="Création de compte"
          :extraction="extraction"
          :submitting="submitting"
          @submit="onProfileSubmit"
          @back="step = Steps.DOCUMENTS"
        />
      </template>
    </div>

    <div class="fixed inset-x-0 bottom-0 z-10">
      <ApplyStepper :steps="stepperSteps" :current="step" />
    </div>
  </div>
</template>
